import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { metricsService } from '../../../services/metricsService';
import { notificationService } from '../../../services/notificationService';

const DashboardMetrics = () => {
  const [metricsData, setMetricsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveUpdateIndicator, setLiveUpdateIndicator] = useState(false);

  useEffect(() => {
    loadMetrics();

    // Subscribe to real-time metrics updates
    const metricsChannel = notificationService?.subscribeToMetrics((updatedMetric) => {
      // Update specific metric in state
      setMetricsData(prev => ({
        ...prev,
        [updatedMetric?.metric_name]: updatedMetric?.metric_value
      }));

      // Show live update indicator
      setLiveUpdateIndicator(true);
      setTimeout(() => setLiveUpdateIndicator(false), 2000);
    });

    // Cleanup subscription
    return () => {
      notificationService?.unsubscribe(metricsChannel);
    };
  }, []);

  const loadMetrics = async () => {
    try {
      const data = await metricsService?.getAllMetrics();
      setMetricsData(data);
    } catch (error) {
      console.error('Failed to load metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const metrics = [
    {
      label: 'Site Traffic',
      value: metricsData?.site_traffic?.toLocaleString() || '0',
      change: '+12.5%',
      trend: 'up',
      icon: 'TrendingUp',
      color: 'primary'
    },
    {
      label: 'Contact Inquiries',
      value: metricsData?.contact_inquiries?.toLocaleString() || '0',
      change: '+8.2%',
      trend: 'up',
      icon: 'MessageSquare',
      color: 'secondary'
    },
    {
      label: 'Blog Engagement',
      value: metricsData?.blog_engagement?.toLocaleString() || '0',
      change: '+15.3%',
      trend: 'up',
      icon: 'BookOpen',
      color: 'success'
    },
    {
      label: 'Project Views',
      value: metricsData?.project_views?.toLocaleString() || '0',
      change: '+9.7%',
      trend: 'up',
      icon: 'Eye',
      color: 'accent'
    }
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {[1, 2, 3, 4]?.map((i) => (
          <div key={i} className="h-32 bg-muted/20 rounded-xl animate-pulse"></div>
        ))}
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Live Update Indicator */}
      {liveUpdateIndicator && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-lg w-fit"
        >
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-sm text-green-500 font-medium">Metrics updated</span>
        </motion.div>
      )}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
      >
        {metrics?.map((metric, index) => (
          <motion.div
            key={index}
            variants={itemVariants}
            className="relative overflow-hidden rounded-xl p-5 sm:p-6 backdrop-blur-xl border border-border/50 touch-manipulation"
            style={{
              background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.9))'
            }}
          >
            {/* Glow Effect */}
            <div
              className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-20 blur-3xl"
              style={{
                background: `var(--color-${metric?.color})`
              }}
            />

            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center"
                  style={{
                    background: `linear-gradient(135deg, var(--color-${metric?.color}), var(--color-secondary))`
                  }}
                >
                  <Icon name={metric?.icon} size={20} color="white" />
                </div>
                <div className="flex items-center gap-1 text-success text-xs sm:text-sm font-medium">
                  <Icon name="TrendingUp" size={14} />
                  {metric?.change}
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground text-xs sm:text-sm">{metric?.label}</p>
                <p className="text-2xl sm:text-3xl font-bold text-foreground">{metric?.value}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};

export default DashboardMetrics;